// ================================================================================
// VariantGridSelector
//
// Manages the interactive grid-based variant selector functionality.
// Handles collection filtering, variant selection, and form synchronization.
//
// Used in: snippets/custom-variant-grid-selector.liquid
// Auto-initializes on page load and in quick-view modals
//
// KEY METHODS:
// ------------
// - handleSizeFilter()       - Filters grid by size tab selection
// - handleCollectionFilter() - Filters grid by collection tab selection
// - handleVariantSelection() - Handles click on variant card
// - selectVariantItem()      - Programmatically selects a variant (used by filters)
// - updateDropdownsForVariant() - Syncs hidden form selects with selected variant
// - updateAddToCartPrice()   - Updates CTA button price on variant change
// - updateDescription()      - Updates product description on variant change
// - applyBVStylesForSelection() - Updates Bazaarvoice ratings on variant change
//
// INITIALIZATION FLOW:
// --------------------
// Page Load:
//   DOMContentLoaded → initializeAll() → finds .variant-grid-selector → creates instances
//
// Modal Open:
//   AJAX loads content → MutationObserver detects → initializeAll() → creates instance
//
// VARIANT CHANGE UPDATES:
// -----------------------
// When a variant is selected (click or programmatic), the following updates occur:
//   1. Visual selection state (border, shadow)
//   2. Hidden dropdown selects (for form submission)
//   3. Add to Cart button price
//   4. Product description (variant metafield or fallback)
//   5. Bazaarvoice ratings widget
// ================================================================================

class VariantGridSelector {
  static preloadedImages = new Set();

  constructor(container) {
    // Store references to DOM elements for this specific selector instance
    this.container = container; // Main container element
    this.gridItems = container.querySelectorAll(".variant-grid-item"); // All variant cards in the grid
    this.collectionTabs = container.querySelectorAll(".collection-tab"); // Collection filter tabs (Core, Limited, etc.)
    this.sizeTabs = container.querySelectorAll(".size-tab"); // Size filter tabs
    this.dropdownSelects = container.querySelectorAll(
      'select[name*="options"]'
    ); // Hidden select elements for form submission
    this.variantsData = null; // Cached variant JSON data

    // Track current filter states
    this.currentSizeFilter = 'all';
    this.currentCollectionFilter = 'all';

    this.init();
  }

  /**
   * Initialize the selector by binding events and setting up initial state
   */
  init() {
    this.bindEvents();
    this.initializeFromUrl();
    this.applyBVStylesForSelection();
  }

  /**
   * Attach event listeners to interactive elements
   */
  bindEvents() {
    // Size tab filtering - when user clicks size tabs
    this.sizeTabs.forEach((tab) => {
      tab.addEventListener("click", (e) => this.handleSizeFilter(e));
    });

    // Collection tab filtering - when user clicks "Core", "Limited", etc.
    this.collectionTabs.forEach((tab) => {
      tab.addEventListener("click", (e) => this.handleCollectionFilter(e));
    });

    // Keep filter buttons in natural tab order while retaining arrow shortcuts
    const sizeTabList = this.container.querySelector('[data-size-tabs]');
    if (sizeTabList) {
      sizeTabList.addEventListener('keydown', (e) => this.handleTabKeydown(e, this.sizeTabs));
    }

    // Keep filter buttons in natural tab order while retaining arrow shortcuts
    const collectionTabList = this.container.querySelector('[data-collection-tabs]');
    if (collectionTabList) {
      collectionTabList.addEventListener('keydown', (e) => this.handleTabKeydown(e, this.collectionTabs));
    }

    // Variant selection - when user clicks on a variant card
    this.gridItems.forEach((item) => {
      this.syncVariantUnavailableState(item);
      item.addEventListener("click", (e) => this.handleVariantSelection(e), true);
      item.addEventListener("mouseenter", () => this.preloadVariantMedia(item));
      item.addEventListener("focus", () => {
        this.syncVariantUnavailableState(item);
        this.preloadVariantMedia(item);
      });
    });
  }

  /**
   * Handle arrow key navigation within a tab group (WCAG 2.1 tab pattern)
   */
  handleTabKeydown(e, tabs) {
    const enabledTabs = Array.from(tabs).filter((tab) => !tab.disabled);
    const currentIndex = enabledTabs.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % enabledTabs.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = enabledTabs.length - 1;
    } else {
      return;
    }

    e.preventDefault();
    enabledTabs[nextIndex].focus();
    enabledTabs[nextIndex].click();
  }
  /**
   * Keep selectable cards exposed correctly to assistive tech
   */
  syncVariantUnavailableState(item) {
    if (!item) return true;

    const isUnavailable = item.disabled;

    item.setAttribute("aria-disabled", isUnavailable ? "true" : "false");
    return isUnavailable;
  }

  /**
   * Return whether a variant card is unavailable for selection
   */
  isVariantUnavailable(item) {
    return this.syncVariantUnavailableState(item);
  }

  /**
   * Preload desktop and mobile images for a variant on hover/focus
   */
  preloadVariantMedia(item) {
    const urls = [
      item.dataset.featuredImageDesktop,
      item.dataset.featuredImageMobile,
    ].filter(Boolean);

    urls.forEach((url) => {
      if (VariantGridSelector.preloadedImages.has(url)) return;
      VariantGridSelector.preloadedImages.add(url);
      const img = new Image();
      img.decoding = "async";
      img.src = url;
      if (img.decode) {
        img.decode().catch(() => {});
      }
    });
  }

  /**
   * Set up initial state when page loads
   * If collection tabs exist, activate the first one and filter accordingly
   */
  initializeFirstTab() {
    // Initialize size filter if size tabs exist
    if (this.sizeTabs.length > 0) {
      const firstSizeTab = this.sizeTabs[0];
      this.currentSizeFilter = firstSizeTab.dataset.size || 'all';
    }

    // Initialize collection filter if collection tabs exist
    if (this.collectionTabs.length > 0) {
      const firstCollectionTab = this.collectionTabs[0];
      this.currentCollectionFilter = firstCollectionTab.dataset.collection || 'all';
    }

    // Update collection tabs availability based on initial size
    this.updateCollectionTabsAvailability();

    // Apply initial filtering
    this.applyFilters();

    // Select the first visible variant and update price
    this.selectFirstVisibleVariant();
  }

  /**
   * Initialize selector based on URL parameters or data attributes
   * Checks for variant parameter in URL and selects it if found
   * Falls back to data-current-variant-id attribute if no URL parameter
   */
  initializeFromUrl() {
    // Get variant ID from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    let variantId = urlParams.get('variant');

    // If no URL parameter, check the container's data-current-variant-id attribute
    // This is set by the Liquid template and represents the selected_or_first_available_variant
    if (!variantId && this.container.dataset.currentVariantId) {
      variantId = this.container.dataset.currentVariantId;
    }

    if (variantId) {
      // Find the grid item matching this variant ID
      const targetItem = Array.from(this.gridItems).find(
        (item) => item.dataset.variantId === variantId
      );

      if (targetItem) {
        // Get the variant's size and collection values
        const variantSize = targetItem.dataset.variantSize;
        const variantType = targetItem.dataset.variantType;

        // Set the size tab if the variant has a size
        if (variantSize && this.sizeTabs.length > 0) {
          this.setActiveTab(this.sizeTabs, variantSize, 'size');
          this.currentSizeFilter = variantSize;
        } else {
          // Default to first size tab
          this.initializeFirstTab();
        }

        // Set the collection tab if the variant has a collection type
        if (variantType && this.collectionTabs.length > 0) {
          this.setActiveTab(this.collectionTabs, variantType, 'collection');
          this.currentCollectionFilter = variantType;
        }

        // Update collection tabs availability based on selected size
        this.updateCollectionTabsAvailability();

        // Apply filters to show the correct variants
        this.applyFilters();

        // Select the variant visually
        this.selectVariantItem(targetItem);
      } else {
        // Variant not found, use default initialization
        this.initializeFirstTab();
      }
    } else {
      // No variant parameter, use default initialization
      this.initializeFirstTab();
    }
  }

  /**
   * Set a tab as active based on its data attribute value
   */
  setActiveTab(tabs, targetValue, tabType) {
    const activeClasses = ["active", "tw:text-white", "tw:bg-blue", "tw:font-bold"];
    const inactiveClasses = ["tw:text-blue", "tw:font-bold"];

    tabs.forEach((tab) => {
      const tabValue = tabType === 'size' ? tab.dataset.size : tab.dataset.collection;
      const isActive = tabValue === targetValue;

      if (isActive) {
        tab.classList.remove(...inactiveClasses);
        tab.classList.add(...activeClasses);
        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('tabindex', '0');
      } else {
        tab.classList.remove(...activeClasses);
        tab.classList.add(...inactiveClasses);
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '0');
      }
    });
  }

  /**
   * Select a variant item visually without triggering the full click handler
   */
  selectVariantItem(item) {
    if (this.isVariantUnavailable(item)) return;

    const selectedClasses = ["selected", "tw:border-black", "tw:shadow-md"];
    const unselectedClasses = ["tw:border-transparent", "tw:shadow-none"];

    // Update visual selection state - remove selection from all, add to target
    this.gridItems.forEach((gridItem) => {
      gridItem.classList.remove(...selectedClasses);
      gridItem.classList.add(...unselectedClasses);
      gridItem.setAttribute("aria-checked", "false");
    });
    item.classList.add(...selectedClasses);
    item.classList.remove(...unselectedClasses);
    item.setAttribute("aria-checked", "true");

    // Update the dropdowns to match this variant
    const variantId = item.dataset.variantId;
    if (variantId) {
      this.updateDropdownsForVariant(variantId);
      this.updateAddToCartPrice(variantId);
    }

    this.updateDescription(item);
    this.applyBVStylesForSelection(item);
  }

  /**
   * Find a variant grid item matching the option value and size
   * Used when switching sizes to maintain the same scent selection
   */
  findVariantByOptionAndSize(optionValue, size) {
    return Array.from(this.gridItems).find((item) => {
      const matchesOption = item.dataset.optionValue === optionValue;
      const matchesSize = item.dataset.variantSize === size;
      const matchesCollection =
        this.currentCollectionFilter === 'all' ||
        item.dataset.variantType === this.currentCollectionFilter;
      const isVisible = !item.classList.contains('hidden');
      const isSelectable = !this.isVariantUnavailable(item);

      return matchesOption && matchesSize && matchesCollection && isVisible && isSelectable;
    });
  }

  /**
   * Select the first visible variant in the grid, preferring in-stock variants
   * Used as fallback when no specific variant match is found
   */
  selectFirstVisibleVariant() {
    const visibleItems = Array.from(this.gridItems).filter((item) => {
      const isVisible = !item.classList.contains('hidden');
      return isVisible && !this.isVariantUnavailable(item);
    });

    const firstVisible =
      visibleItems.find((item) => !item.classList.contains('is-sold-out')) ||
      visibleItems[0];

    if (firstVisible) {
      this.selectVariantItem(firstVisible);
    }
  }

  /**
   * Handle size tab clicks
   * Updates tab visual state and filters variants
   */
  handleSizeFilter(e) {
    const clickedTab = e.currentTarget;
    const filterSize = clickedTab.dataset.size;
    if (!filterSize) return;

    // Remember the currently selected variant's option value (scent name)
    const currentlySelected = this.container.querySelector(".variant-grid-item.selected");
    const currentOptionValue = currentlySelected?.dataset.optionValue;

    // Define CSS classes for active/inactive tab states
    const activeClasses = ["active", "tw:text-white", "tw:bg-blue", "tw:font-bold"];
    const inactiveClasses = ["tw:text-blue", "tw:font-bold"];

    // Update tab visual states and ARIA attributes
    this.sizeTabs.forEach((tab) => {
      tab.classList.remove(...activeClasses);
      tab.classList.add(...inactiveClasses);
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '0');
    });
    clickedTab.classList.remove(...inactiveClasses);
    clickedTab.classList.add(...activeClasses);
    clickedTab.setAttribute('aria-selected', 'true');
    clickedTab.setAttribute('tabindex', '0');

    // Update current filter state and apply
    this.currentSizeFilter = filterSize;

    // Update collection tabs availability based on new size
    this.updateCollectionTabsAvailability();

    this.applyFilters();

    // Try to find and select the same scent in the new size
    if (currentOptionValue) {
      const matchingVariant = this.findVariantByOptionAndSize(currentOptionValue, filterSize);
      if (matchingVariant) {
        this.selectVariantItem(matchingVariant);
      } else {
        // If exact match not found, select first visible variant
        this.selectFirstVisibleVariant();
      }
    } else {
      // No previous selection, select first visible variant
      this.selectFirstVisibleVariant();
    }

    // Update the hidden size dropdown
    this.dropdownSelects.forEach((select) => {
      if (select.name && select.name.toLowerCase().includes('size')) {
        for (let option of select.options) {
          const handleized = option.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
          if (handleized === filterSize) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }
    });
  }

  /**
   * Handle collection tab clicks (Core, Limited, etc.)
   * Updates tab visual state and filters variants
   */
  handleCollectionFilter(e) {
    const clickedTab = e.currentTarget;

    // Don't allow clicking disabled tabs
    if (clickedTab.disabled) return;

    const filterCollection = clickedTab.dataset.collection; // Get which collection was clicked
    if (!filterCollection) return;

    // Define CSS classes for active/inactive tab states
    const activeClasses = ["active", "tw:text-white", "tw:bg-blue", "tw:font-bold"];
    const inactiveClasses = ["tw:text-blue", "tw:font-bold"];

    // Update tab visual states and ARIA attributes
    this.collectionTabs.forEach((tab) => {
      if (!tab.disabled) {
        tab.classList.remove(...activeClasses);
        tab.classList.add(...inactiveClasses);
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '0');
      }
    });
    clickedTab.classList.remove(...inactiveClasses);
    clickedTab.classList.add(...activeClasses);
    clickedTab.setAttribute('aria-selected', 'true');
    clickedTab.setAttribute('tabindex', '0');

    // Update current filter state and apply
    this.currentCollectionFilter = filterCollection;
    this.applyFilters();
  }

  /**
   * Update collection tabs based on current size filter
   * Disables collection tabs that have no variants available in the selected size
   */
  updateCollectionTabsAvailability() {
    if (!this.collectionTabs.length) return;

    // Count available variants per collection in the current size
    const availableCounts = {};
    this.gridItems.forEach((item) => {
      const itemSize = item.dataset.variantSize || '';
      const itemType = item.dataset.variantType || '';
      const matchesSize = this.currentSizeFilter === "all" || !this.currentSizeFilter || itemSize === this.currentSizeFilter;
      if (matchesSize && itemType) {
        availableCounts[itemType] = (availableCounts[itemType] || 0) + 1;
      }
    });

    let currentCollectionAvailable = false;
    let firstAvailableCollection = null;

    this.collectionTabs.forEach((tab) => {
      const tabCollection = tab.dataset.collection;
      const available = (availableCounts[tabCollection] || 0) > 0;
      tab.disabled = !available;
      tab.setAttribute('tabindex', available ? '0' : '-1');
      tab.classList.toggle('tw:cursor-not-allowed', !available);
      tab.classList.toggle('tw:cursor-pointer', available);
      if (available) {
        if (tabCollection === this.currentCollectionFilter) currentCollectionAvailable = true;
        if (!firstAvailableCollection) firstAvailableCollection = tabCollection;
      }
    });

    // If active collection became unavailable, switch to first available
    if (!currentCollectionAvailable && firstAvailableCollection) {
      this.currentCollectionFilter = firstAvailableCollection;
      this.setActiveTab(this.collectionTabs, firstAvailableCollection, 'collection');
    }
  }

  /**
   * Apply filters based on both size and collection selections
   * This is the unified filtering method that considers both filter types
   */
  applyFilters() {
    // console.log('🔍 Applying filters:', {
    //   sizeFilter: this.currentSizeFilter,
    //   collectionFilter: this.currentCollectionFilter
    // });

    // Filter grid items (variant cards)
    this.gridItems.forEach((item) => {
      const itemSize = item.dataset.variantSize || ''; // Single size value
      const itemType = item.dataset.variantType || ''; // Collection type from liquid template

      // Check if item matches size filter
      const matchesSize = this.currentSizeFilter === "all" ||
                          !this.currentSizeFilter ||
                          itemSize === this.currentSizeFilter;

      // Check if item matches collection filter
      const matchesCollection = this.currentCollectionFilter === "all" ||
                                !this.currentCollectionFilter ||
                                itemType === this.currentCollectionFilter;

      // Show item only if it matches BOTH filters (AND logic)
      if (matchesSize && matchesCollection) {
        item.classList.remove("hidden");
      } else {
        item.classList.add("hidden");
      }
    });

    // Update scroll behavior based on how many items are now visible
    this.updateScrollBehavior();

    // Filter dropdown options to match visible grid items
    // This keeps the hidden form selects in sync with the visual grid
    this.dropdownSelects.forEach((select) => {
      const options = select.querySelectorAll("option");

      options.forEach((option) => {
        // Always keep the empty/default option visible
        if (option.value === "") {
          option.style.display = "";
          return;
        }

        // Find any visible grid item with this option value
        const hasVisibleMatch = Array.from(this.gridItems).some(
          (item) => item.dataset.optionValue === option.value &&
                   !item.classList.contains("hidden")
        );

        // Show/hide option based on whether any matching grid item is visible
        option.style.display = hasVisibleMatch ? "" : "none";
      });

      // Reset dropdown selection if currently selected option is now hidden
      if (
        select.selectedOptions[0] &&
        select.selectedOptions[0].style.display === "none"
      ) {
        select.selectedIndex = 0; // Reset to first option (usually empty)
      }
    });
  }

  /**
   * Manage scroll behavior based on number of visible variants
   * If more than 8 variants are visible, add scrolling to prevent excessive height
   */
  updateScrollBehavior() {
    const visibleItems = Array.from(this.gridItems).filter(
      (item) => !item.classList.contains("hidden")
    );

    const container = this.container.querySelector(".variant-grid-container");

    // Add scrolling if too many variants are visible
    if (visibleItems.length > 8) {
      container.classList.add("scrollable");
    } else {
      container.classList.remove("scrollable");
    }
  }

  /**
   * Handle variant card selection
   * Updates visual state and synchronizes with form inputs
   */
  handleVariantSelection(e) {
    const button = e.currentTarget;

    if (this.isVariantUnavailable(button)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    const optionValue = button.dataset.optionValue; // The variant name (e.g., "Vanilla")
    const swatchColor = button.dataset.swatchColor; // Metaobject swatch color
    const variantId = button.dataset.variantId; // The specific variant ID

    // Update visual selection state - remove selection from all, add to clicked
    const selectedClasses = ["selected", "tw:border-black", "tw:shadow-md"];
    const unselectedClasses = ["tw:border-transparent", "tw:shadow-none"];

    this.gridItems.forEach((item) => {
      item.classList.remove(...selectedClasses);
      item.classList.add(...unselectedClasses);
      item.setAttribute("aria-checked", "false");
    });
    button.classList.add(...selectedClasses);
    button.classList.remove(...unselectedClasses);
    button.setAttribute("aria-checked", "true");

    // Use the variant ID from the button directly
    // This ensures we always select the exact variant that was clicked
    if (variantId) {
      this.updateDropdownsForVariant(variantId);
      this.updateAddToCartPrice(variantId);
    } else {
      // Fallback to old logic if variant ID is not available
      const optionSelect = this.container.querySelector(
        'select[name*="options"]'
      );
      if (optionSelect) {
        optionSelect.value = optionValue;

        // Trigger change event so theme's JavaScript can respond
        // This ensures cart functionality, price updates, etc. still work
        const changeEvent = new Event("change", { bubbles: true });
        optionSelect.dispatchEvent(changeEvent);
      }

      // Find the correct variant based on ALL current dropdown selections
      const correctVariantId = this.findVariantByAllSelections();

      // Update all dropdowns to match the found variant
      if (correctVariantId) {
        this.updateDropdownsForVariant(correctVariantId);
        this.updateAddToCartPrice(correctVariantId);
      }
    }

    this.updateDescription(button);
    this.applyBVStylesForSelection(button);
  }

  /**
   * Find variant matching all current dropdown selections
   */
  findVariantByAllSelections() {
    const variants = this.getVariantsData();
    if (!variants.length) return null;

    const selectedOptions = {};
    this.dropdownSelects.forEach((select) => {
      const dataIndex = select.getAttribute("data-index");
      if (dataIndex && select.value) {
        const optionNum = dataIndex.replace("option", "");
        selectedOptions[`option${optionNum}`] = select.value;
      }
    });

    const match = variants.find((v) =>
      Object.keys(selectedOptions).every((key) => v[key] === selectedOptions[key])
    );

    return match ? match.id : null;
  }

  /**
   * Update all dropdown selects to match a specific variant
   * Used when user selects a variant that has multiple option combinations
   */
  updateDropdownsForVariant(variantId) {
    // Get variant data from the JSON script injected by liquid template
    const variants = this.getVariantsData();
    if (!variants.length) return;

    const selectedVariant = variants.find((v) => v.id == variantId);
    if (!selectedVariant) return;

    // Update all dropdown selects to match this variant's option values
    // This ensures that if someone selects "Vanilla - Limited Edition",
    // both the scent dropdown (Vanilla) and collection dropdown (Limited) get updated
    this.dropdownSelects.forEach((select) => {
      const dataIndex = select.getAttribute("data-index");

      if (dataIndex) {
        // Extract option number from data-index (e.g., "option1" -> "1")
        const optionNumber = dataIndex.replace("option", "");
        const variantOptionValue = selectedVariant[`option${optionNumber}`];

        // Update select value if it's different and trigger change event
        if (variantOptionValue && select.value !== variantOptionValue) {
          select.value = variantOptionValue;

          // Trigger change event for theme compatibility
          const changeEvent = new Event("change", { bubbles: true });
          select.dispatchEvent(changeEvent);
        }
      }
    });
  }

  /**
   * Return parsed variant JSON data for the current grid, cached per instance.
   */
  getVariantsData() {
    if (this.variantsData) return this.variantsData;

    const variantJsonScript = this.container.querySelector(
      "script[data-variant-json]"
    );
    if (!variantJsonScript) return [];

    try {
      this.variantsData = JSON.parse(variantJsonScript.textContent);
    } catch (error) {
      console.error("Error parsing variant JSON data:", error);
      this.variantsData = [];
    }

    return this.variantsData;
  }

  /**
   * Update the Add to Cart button price based on selected variant
   */
  updateAddToCartPrice(variantId) {
    const variant = this.getVariantsData().find((v) => String(v.id) === String(variantId));
    if (!variant) return;

    const priceEl = this.container.closest(".quick-product-modal, .quick-product")?.querySelector("[data-add-to-cart-price]");
    if (priceEl) priceEl.textContent = `$${(variant.price / 100).toFixed(2)}`;
  }

  /**
   * Update the product description based on selected variant
   */
  updateDescription(item) {
    const modalEl = this.container.closest(".quick-product-modal, .quick-product");
    const descEl = modalEl?.querySelector(".product__description .rte");
    if (!descEl) return;

    const variantDesc = item.dataset.variantDescription;
    const defaultDesc = this.container.dataset.defaultDescription;
    descEl.innerHTML = variantDesc || defaultDesc || '';
  }

  /**
   * Update Bazaarvoice widget visibility and styling for the current quick modal selection.
   */
  applyBVStylesForSelection(selectedItem) {
    const modalEl = this.container.closest(".quick-product-modal");
    if (!modalEl) return;

    const item =
      selectedItem ||
      this.container.querySelector(".variant-grid-item.selected") ||
      Array.from(this.gridItems).find(
        (gridItem) => !gridItem.classList.contains("hidden")
      );

    if (!item) return;

    const variantId = item.dataset.variantId;
    const swatchColor = item.dataset.swatchColor;
    const variants = this.getVariantsData();
    const selectedVariant = variants.find((v) => String(v.id) === variantId);
    const sku = selectedVariant?.sku;

    VariantGridSelector.updateQuickModalBV({
      sku,
      swatchColor,
      context: modalEl,
    });
  }

  // ============================================================================
  // Static initialization method
  // ============================================================================
  static initializeAll() {
    const selectors = document.querySelectorAll(".variant-grid-selector");
    let count = 0;

    selectors.forEach((selector) => {
      if (!selector.hasAttribute("data-variant-grid-initialized")) {
        selector.setAttribute("data-variant-grid-initialized", "true");
        new VariantGridSelector(selector);
        count++;
      }
    });

    // To avoid adding new file, adding the quick product modal BV modifications here
    // TODO: add any other function for the BV stars here, ie: color change
    try {
      function waitForElement(selector) {
        return new Promise((resolve) => {
          if (document.querySelector(selector)) {
            return resolve(document.querySelector(selector));
          }

          const observer = new MutationObserver((mutations) => {
            if (document.querySelector(selector)) {
              observer.disconnect();
              resolve(document.querySelector(selector));
            }
          });

          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });
        });
      }

      async function adjustBVSummaryStyles() {
        const bvMainContainerEl = await waitForElement(".bv_main_container");
        const firstStarSvgEl = await waitForElement(
          ".bv_stars_component_container svg"
        );
        const starSvgEls = bvMainContainerEl.querySelectorAll(
          ".bv_stars_component_container svg:not(:last-child)"
        );
        if (starSvgEls.length) {
          starSvgEls.forEach((star) => {
            let newStarStyle = "padding-right: 10px !important;";
            star.setAttribute("style", newStarStyle);
            star.setAttribute("width", "16px");
            star.setAttribute("height", "15px");
          });
          const lastStarSvgEl = bvMainContainerEl.querySelector(
            ".bv_stars_component_container svg:last-child"
          );
          lastStarSvgEl.setAttribute("width", "16px");
          lastStarSvgEl.setAttribute("height", "15px");
        }
        const bvStarsComponentContainerEl = bvMainContainerEl.querySelector(
          ".bv_stars_component_container"
        );
        bvStarsComponentContainerEl.setAttribute(
          "style",
          "padding-right: 20px !important;"
        );

        const textEls = bvMainContainerEl.querySelectorAll(
          ".bv_avgRating_component_container, .bv_numReviews_text"
        );
        if (textEls.length) {
          textEls.forEach((textEl) => {
            let newTextStyle =
              "font-family: 'BrandonGrotesque', sans-serif !important; font-weight: 700 !important; font-size: 12px !important; line-height: 130% !important; letter-spacing: 1.2px !important; text-transform: uppercase !important; color: var(--color-native-navy) !important;";
            textEl.setAttribute("style", newTextStyle);
          });
        }
      }
      adjustBVSummaryStyles();
    } catch (error) {
      console.log("Error updating BV stars on modal:", error);
    }

    return count;
  }

  // ============================================================================
  // Bazaarvoice helpers for quick product modal
  // ============================================================================

  static waitForElement(selector, root = document) {
    return new Promise((resolve) => {
      const scope = root || document;
      const found = scope.querySelector(selector);
      if (found) {
        return resolve(found);
      }

      const targetNode =
        scope === document ? document.documentElement : scope;
      const observer = new MutationObserver(() => {
        const el = scope.querySelector(selector);
        if (el) {
          observer.disconnect();
          resolve(el);
        }
      });

      observer.observe(targetNode, {
        childList: true,
        subtree: true,
      });
    });
  }

  static getFallbackStarColor() {
    const cssVar = getComputedStyle(
      document.documentElement
    ).getPropertyValue("--color-products-rating-star");
    const cleaned = (cssVar || "").trim();
    return cleaned || "#d2d2d2";
  }

  static refreshBV() {
    if (window.BV && typeof window.BV.reveal === "function") {
      window.BV.reveal();
    } else if (window.bvCallback) {
      window.bvCallback();
    }
  }

  static async updateBVStarsColor(newColor, bvContainer) {
    const container =
      bvContainer || (await VariantGridSelector.waitForElement(".bv_main_container"));
    if (!container) return;

    const reviewStarsEls = container.querySelectorAll('[offset="0%"]');
    reviewStarsEls.forEach((star) => {
      star.setAttribute(
        "style",
        `stop-color: ${newColor}; stop-opacity: 1;`
      );
    });

    container.querySelectorAll('[offset="1%"]').forEach((stop) => {
      stop.setAttribute("style", "stop-color: transparent; stop-opacity: 0;");
    });
    container.querySelectorAll(".bv_stars_component_container path").forEach((path) => {
      path.style.stroke = "#000000";
      path.style.strokeWidth = "0.8";
    });
  }

  static async adjustBVSummaryStyles(bvContainer) {
    const container =
      bvContainer || (await VariantGridSelector.waitForElement(".bv_main_container"));
    if (!container) return;

    const starSvgEls = container.querySelectorAll(
      ".bv_stars_component_container svg:not(:last-child)"
    );
    if (starSvgEls.length) {
      starSvgEls.forEach((star) => {
        let newStarStyle = "padding-right: 10px !important;";
        star.setAttribute("style", newStarStyle);
        star.setAttribute("width", "16px");
        star.setAttribute("height", "15px");
      });
      const lastStarSvgEl = container.querySelector(
        ".bv_stars_component_container svg:last-child"
      );
      if (lastStarSvgEl) {
        lastStarSvgEl.setAttribute("width", "16px");
        lastStarSvgEl.setAttribute("height", "15px");
      }
    }
    const bvStarsComponentContainerEl = container.querySelector(
      ".bv_stars_component_container"
    );
    if (bvStarsComponentContainerEl) {
      bvStarsComponentContainerEl.setAttribute(
        "style",
        "padding-right: 20px !important;"
      );
    }

    const textEls = container.querySelectorAll(
      ".bv_avgRating_component_container, .bv_numReviews_text"
    );
    if (textEls.length) {
      textEls.forEach((textEl) => {
        let newTextStyle =
          "font-family: 'BrandonGrotesque', sans-serif !important; font-weight: 450 !important; font-size: 12px !important; line-height: 130% !important; letter-spacing: 1.2px !important; text-transform: uppercase !important; color: var(--color-native-navy) !important;";
        textEl.setAttribute("style", newTextStyle);
      });
    }
  }

  static async updateQuickModalBV({ sku, swatchColor, context }) {
    const modalEl =
      context?.closest?.(".quick-product-modal") ||
      document.querySelector(".quick-product-modal");
    if (!modalEl) return;

    const ratingWidget = modalEl.querySelector('[data-bv-show="rating_summary"]');
    if (!ratingWidget) return;

    const targetColor =
      (swatchColor || "").trim() || VariantGridSelector.getFallbackStarColor();

    if (sku) {
      ratingWidget.setAttribute("data-bv-product-id", sku);
      ratingWidget.removeAttribute("data-bv-ready");
    }

    ratingWidget.style.visibility = "hidden";
    ratingWidget.style.minHeight = "42px";

    VariantGridSelector.refreshBV();

    let painted = false;
    const paint = async () => {
      const bvContainer = await VariantGridSelector.waitForElement(
        ".bv_main_container",
        ratingWidget
      );
      if (!bvContainer) return false;

      await VariantGridSelector.adjustBVSummaryStyles(bvContainer);
      await VariantGridSelector.updateBVStarsColor(targetColor, bvContainer);
      ratingWidget.style.visibility = "";
      painted = true;
      return true;
    };

    paint();

    const widgetObserver = new MutationObserver(() => paint());
    widgetObserver.observe(ratingWidget, { childList: true, subtree: true });

    setTimeout(() => {
      widgetObserver.disconnect();
      ratingWidget.style.visibility = "";
    }, 5000);
  }
}

// ============================================================================
// Auto-Initialization
// ============================================================================

// Initialize on page load
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () =>
    VariantGridSelector.initializeAll()
  );
} else {
  VariantGridSelector.initializeAll();
}

// Reinitialize when modal content loads
const modalObserver = new MutationObserver(() => {
  const modalContent = document.querySelector(
    ".quick-product-modal__content:not(.empty)"
  );
  if (modalContent && modalContent.querySelector(".variant-grid-selector")) {
    VariantGridSelector.initializeAll();
  }
});

// Start observing modal for changes
const startObserving = () => {
  const modal = document.querySelector("[data-quick-product-modal]");
  if (modal) {
    modalObserver.observe(modal, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class"],
    });
  } else {
    // Modal not ready yet, try again
    setTimeout(startObserving, 500);
  }
};

startObserving();
